import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "textarea", "preview"]

  open() {
    // Remember which editor to insert into (before dialog steals focus)
    this._editor = this.element.querySelector("lexxy-editor")
    this.dialogTarget.showModal()
    this.textareaTarget.value = this.defaultExample
    this.renderPreview()
  }

  close() {
    this.dialogTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  inputChanged() {
    clearTimeout(this._debounce)
    this._debounce = setTimeout(() => this.renderPreview(), 400)
  }

  async renderPreview() {
    const source = this.textareaTarget.value.trim()
    if (!source) {
      this.previewTarget.innerHTML = '<p class="text-text-secondary text-sm">Inserisci codice mermaid...</p>'
      return
    }

    try {
      const { default: mermaid } = await import("mermaid")
      mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "loose" })
      const id = `mermaid-preview-${crypto.randomUUID().slice(0, 8)}`
      const { svg } = await mermaid.render(id, source)
      this.previewTarget.innerHTML = svg
    } catch {
      this.previewTarget.innerHTML = '<p class="text-error text-sm">Sintassi mermaid non valida</p>'
    }
  }

  async insert() {
    const source = this.textareaTarget.value.trim()
    if (!source) return

    const editor = this._editor
    if (!editor?.contents) return

    try {
      // Render mermaid to SVG
      const { default: mermaid } = await import("mermaid")
      mermaid.initialize({ startOnLoad: false, theme: "neutral", securityLevel: "loose" })
      const id = `mermaid-insert-${crypto.randomUUID().slice(0, 8)}`
      const { svg } = await mermaid.render(id, source)

      // Convert SVG to PNG via canvas
      const pngBlob = await this.svgToPng(svg)
      const file = new File([pngBlob], "diagramma-mermaid.png", { type: "image/png" })

      // Focus editor and upload
      editor.focus()
      await new Promise(r => setTimeout(r, 50))
      editor.contents.uploadFile(file)

      this.close()
    } catch (e) {
      console.error("Mermaid insert failed:", e)
    }
  }

  svgToPng(svgString) {
    return new Promise((resolve, reject) => {
      // Parse SVG to get real dimensions from viewBox or width/height attributes
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(svgString, "image/svg+xml")
      const svgEl = svgDoc.querySelector("svg")
      if (!svgEl) return reject(new Error("No SVG element found"))

      let width, height
      const viewBox = svgEl.getAttribute("viewBox")
      if (viewBox) {
        const parts = viewBox.split(/[\s,]+/)
        width = parseFloat(parts[2])
        height = parseFloat(parts[3])
      }
      // Fallback to width/height attributes
      if (!width) width = parseFloat(svgEl.getAttribute("width")) || 800
      if (!height) height = parseFloat(svgEl.getAttribute("height")) || 600

      // Force explicit dimensions on the SVG so the image renders at the correct size
      svgEl.setAttribute("width", width)
      svgEl.setAttribute("height", height)
      const fixedSvg = new XMLSerializer().serializeToString(svgEl)

      const img = new Image()

      img.onload = () => {
        const scale = 2
        const canvas = document.createElement("canvas")
        canvas.width = width * scale
        canvas.height = height * scale
        const ctx = canvas.getContext("2d")
        ctx.fillStyle = "#ffffff"
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(blob => {
          if (blob) resolve(blob)
          else reject(new Error("Canvas toBlob failed"))
        }, "image/png")
      }

      img.onerror = () => reject(new Error("SVG image load failed"))

      const encoded = btoa(unescape(encodeURIComponent(fixedSvg)))
      img.src = `data:image/svg+xml;base64,${encoded}`
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  get defaultExample() {
    return `graph TD
    A[Inizio] --> B[Analisi]
    B --> C[Sviluppo]
    C --> D[Testing]
    D --> E[Deploy]`
  }
}
